/**
 * MuscularClinicalEnginePage — Clinical Engine UI за Muscular IR модула.
 *
 * Peer-review V1-V6 closed. Frozen at v1.4.0 (SHA-256 bd9dd438...f518c8).
 * Engine is a DECISION-SUPPORT LOGIC FRAMEWORK, not empirically validated.
 *
 * V5 Critical #1 operationalization:
 *   PRIMARY output = activation_band (colored chip)
 *   SECONDARY output = activation_pct (behind "Show audit details" toggle)
 *   Disclaimer text always visible.
 *
 * Data source: 4 new /api/clinical-engine/muscular/* endpoints (Etap 6 freeze).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Loader2, ArrowLeft, Activity, ShieldAlert, Lock, FileText,
  Globe2, ChevronDown, ChevronRight, AlertTriangle, BookOpen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clinical-engine/muscular`;

// ─────────────────────────────────────────────────────────────
// V5 C1 — Band styling (5 bands)
// ─────────────────────────────────────────────────────────────
const BAND_STYLES = {
  negligible: { bg: "bg-emerald-500",  text: "text-emerald-700",  ring: "ring-emerald-200",  border: "border-emerald-300" },
  low:        { bg: "bg-teal-500",     text: "text-teal-700",     ring: "ring-teal-200",     border: "border-teal-300" },
  moderate:   { bg: "bg-amber-500",    text: "text-amber-700",    ring: "ring-amber-200",    border: "border-amber-300" },
  elevated:   { bg: "bg-orange-500",   text: "text-orange-700",   ring: "ring-orange-200",   border: "border-orange-300" },
  high:       { bg: "bg-rose-500",     text: "text-rose-700",     ring: "ring-rose-200",     border: "border-rose-300" },
};

// ─────────────────────────────────────────────────────────────
// Reusable AuditToggle — hides numeric/raw audit info behind a toggle
// (V5 C1 — clinician should see band first, numeric second)
// ─────────────────────────────────────────────────────────────
const AuditToggle = ({ children, label = "Покажи audit детайли" }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="audit-toggle"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {label}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-slate-50 rounded-md border border-slate-200 text-xs text-slate-600">
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// BandChip — V5 C1 primary clinician-facing output
// ─────────────────────────────────────────────────────────────
const BandChip = ({ band, labelBg, labelEn }) => {
  const style = BAND_STYLES[band] || BAND_STYLES.moderate;
  return (
    <div
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full ${style.bg} text-white font-semibold shadow-sm ring-4 ${style.ring}`}
      data-testid={`band-chip-${band}`}
    >
      <Activity className="w-4 h-4" />
      <span className="text-base">{labelBg}</span>
      <span className="text-xs opacity-80">/ {labelEn}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 1) Module Status Card (freeze manifest + peer-review history)
// ─────────────────────────────────────────────────────────────
const ModuleStatusCard = ({ version }) => {
  if (!version) return null;
  const shaShort = version.freeze_manifest_sha256?.slice(0, 12);
  return (
    <Card className="border-slate-200" data-testid="module-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Lock className="w-5 h-5 text-emerald-600" />
          Muscular IR · Frozen v{version.calibration_version}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-slate-500">Calibration ID:</span>
          <span className="font-mono text-xs">{version.calibration_id}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-slate-500">Манифест SHA-256:</span>
          <span className="font-mono text-xs" title={version.freeze_manifest_sha256}>
            {shaShort}…
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-slate-500">Заключени файлове:</span>
          <span className="font-semibold">{version.file_count}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-slate-500">Frozen at UTC:</span>
          <span className="font-mono text-xs">{version.frozen_at_utc}</span>
        </div>
        <div className="pt-2 mt-2 border-t border-slate-100">
          <div className="text-xs text-slate-500 mb-1.5">Затворени peer-review рунда:</div>
          <div className="flex flex-wrap gap-1.5">
            {(version.peer_review_iterations_closed || []).map((v) => (
              <Badge key={v} variant="outline" className="text-xs bg-emerald-50 border-emerald-200 text-emerald-700">
                ✓ {v}
              </Badge>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-3 mt-3 border-t border-slate-100 text-center">
          <div>
            <div className="text-2xl font-semibold text-slate-700">{version.muscular_rules_count}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Правила</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-slate-700">{version.muscular_domains_count}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Домейни</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-slate-700">{version.muscular_clinical_flags_count}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Клинични флагове</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// 2) Activation Band Demonstrator (V5 C1 interactive demo)
// ─────────────────────────────────────────────────────────────
const ActivationBandDemo = () => {
  const [pct, setPct] = useState(35);
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBand = async (value) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/activation_band`, { params: { activation_pct: value } });
      setBand(data);
    } catch (e) {
      setBand(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBand(pct); /* eslint-disable-next-line */ }, []);

  const handleChange = (val) => {
    const v = Array.isArray(val) ? val[0] : val;
    setPct(v);
    fetchBand(v);
  };

  return (
    <Card className="border-slate-200" data-testid="activation-band-demo">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="w-5 h-5 text-slate-600" />
          Демонстратор: Activation → Band
        </CardTitle>
        <div className="text-xs text-slate-500 mt-1">
          V5 Critical #1 — primary output е <strong>band</strong> (категория), не numeric %.
          Numeric стойността е audit info с disclaimer.
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Primary output — band chip */}
        <div className="flex flex-col items-center py-4 bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-100">
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          ) : band ? (
            <BandChip band={band.band} labelBg={band.label_bg} labelEn={band.label_en} />
          ) : (
            <div className="text-sm text-slate-400">Изчакване…</div>
          )}
          <div className="text-xs text-slate-400 mt-3 max-w-xs text-center">
            ↑ Това е <strong>primary output</strong> за клинициста
          </div>
        </div>

        {/* Slider — adjusts the secondary numeric input */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500 uppercase tracking-wide">
              Internal audit number
            </span>
            <span className="font-mono text-sm text-slate-700">{pct.toFixed(0)}%</span>
          </div>
          <Slider
            value={[pct]}
            min={0}
            max={100}
            step={1}
            onValueChange={handleChange}
            className="w-full"
            data-testid="activation-pct-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>0</span><span>15</span><span>30</span><span>50</span><span>70</span><span>100</span>
          </div>
        </div>

        {/* Band visual scale */}
        <div className="grid grid-cols-5 gap-1.5">
          {Object.entries(BAND_STYLES).map(([key, st]) => (
            <div
              key={key}
              className={`h-2 rounded-full ${st.bg} ${band?.band === key ? "ring-2 ring-offset-1 " + st.ring : "opacity-30"} transition-all`}
              title={key}
            />
          ))}
        </div>

        {/* Disclaimer */}
        {band?.activation_pct_disclaimer && (
          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 whitespace-pre-line">
              {band.activation_pct_disclaimer}
            </div>
          </div>
        )}

        {/* Audit toggle — hides numeric details */}
        <AuditToggle>
          <div className="space-y-1.5">
            <div><span className="text-slate-500">activation_pct:</span> <span className="font-mono">{pct.toFixed(1)}%</span></div>
            <div><span className="text-slate-500">band:</span> <span className="font-mono">{band?.band}</span></div>
            <div><span className="text-slate-500">primary clinician field:</span> <span className="font-mono">activation_band</span></div>
            <div><span className="text-slate-500">secondary audit field:</span> <span className="font-mono">activation_pct</span></div>
          </div>
        </AuditToggle>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// 3) Population Overlay Browser
// ─────────────────────────────────────────────────────────────
const POPULATIONS = [
  { code: "european",    label: "Европейски (default)" },
  { code: "japanese",    label: "Японски (AWGS 2019)" },
  { code: "korean",      label: "Корейски (AWGS 2019)" },
  { code: "han_chinese", label: "Хан китайски (AWGS 2019)" },
  { code: "south_asian", label: "Южно-азиатски (роадмап)" },
];

const PopulationOverlayBrowser = () => {
  const [population, setPopulation] = useState("japanese");
  const [overlay, setOverlay] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = async (p) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/population_overlay`, { params: { population: p } });
      setOverlay(data);
    } catch (e) {
      setOverlay(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch_(population); /* eslint-disable-next-line */ }, [population]);

  return (
    <Card className="border-slate-200" data-testid="population-overlay-browser">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Globe2 className="w-5 h-5 text-slate-600" />
          Population Overlay (V6 #4)
        </CardTitle>
        <div className="text-xs text-slate-500 mt-1">
          Cut-offs варират по етничност. AWGS 2019 за East Asian е populated като worked example.
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {POPULATIONS.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => setPopulation(p.code)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                population === p.code
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`pop-${p.code}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : overlay?.overlay_id ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">
                {overlay.overlay_id} · v{overlay.version}
              </Badge>
            </div>
            <div className="space-y-2">
              {Object.entries(overlay.threshold_overrides || {}).map(([rule, info]) => (
                <div key={rule} className="p-3 bg-slate-50 rounded-md border border-slate-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono text-xs font-semibold text-slate-700">{rule}</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-2 line-clamp-3">{info.rationale?.split("\n")[0]}</div>
                  {info.levels && (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(info.levels).slice(0, 2).map(([sex, levels]) => (
                        <div key={sex} className="text-xs">
                          <div className="font-semibold capitalize text-slate-600">{sex === "male" ? "Мъже" : "Жени"}:</div>
                          {Array.isArray(levels) ? (
                            <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                              high: &lt;{levels.find(l => l.name === "high")?.max ?? "?"}
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-400">(age-stratified)</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {overlay.source_references?.length > 0 && (
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                <strong>Източници:</strong>{" "}
                {overlay.source_references.map((s) => `PMID ${s.pmid}`).join(", ")}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 rounded-md text-sm text-slate-600">
            <div className="font-semibold mb-1">⚠️ Няма populated overlay за тази етничност</div>
            <div className="text-xs">Fallback: {overlay?.fallback || "european_default_thresholds"}</div>
            {overlay?.note && <div className="text-xs mt-2 text-slate-500">{overlay.note}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// 4) Limitations Declaration (L1-L10) — prominent display
// ─────────────────────────────────────────────────────────────
const LimitationsCard = ({ limitations }) => {
  const [expanded, setExpanded] = useState(false);
  if (!limitations) return null;
  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="limitations-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-amber-900">
          <ShieldAlert className="w-5 h-5 text-amber-600" />
          Декларация за ограничения
        </CardTitle>
        <div className="text-xs text-amber-700 mt-1">
          Engine status: <strong>{limitations.engine_status}</strong> · validation:{" "}
          <strong className="text-rose-700">{limitations.empirical_validation_status}</strong>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-amber-900 mb-3 font-semibold">
          Activation interpretation: {limitations.activation_interpretation}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
          data-testid="limitations-expand"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expanded ? "Скрий" : "Покажи"} L1-L10 + peer-review история
        </button>
        {expanded && (
          <div className="mt-3 space-y-3">
            <div>
              <div className="text-xs font-semibold text-amber-900 mb-1.5">Структурни ограничения (L1-L10):</div>
              <ul className="space-y-1">
                {(limitations.structural_limitations || []).map((l, i) => (
                  <li key={i} className="text-[11px] text-amber-800 leading-snug">• {l}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold text-amber-900 mb-1.5">Peer-review история:</div>
              <ul className="space-y-1.5">
                {Object.entries(limitations.peer_review_history || {}).map(([v, summary]) => (
                  <li key={v} className="text-[11px] text-amber-800 leading-snug">
                    <Badge variant="outline" className="mr-1.5 text-[9px] bg-white border-amber-300 text-amber-700">
                      {v}
                    </Badge>
                    {summary}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
export default function MuscularClinicalEnginePage() {
  const navigate = useNavigate();
  const { token } = useAuth?.() || {};
  const [version, setVersion] = useState(null);
  const [limitations, setLimitations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [v, l] = await Promise.all([
          axios.get(`${API}/version`, { headers }),
          axios.get(`${API}/limitations`, { headers }),
        ]);
        setVersion(v.data);
        setLimitations(l.data);
      } catch (e) {
        setError(e.message || "Грешка при зареждане");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="mb-3"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Назад към таблото
            </Button>
            <h1 className="text-3xl font-semibold text-slate-800 tracking-tight">
              Muscular IR · Клиничен Engine
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Decision-support logic framework за периферна (мускулна) инсулинова резистентност.{" "}
              <strong>Не е empirically validated diagnostic.</strong> 6 peer-review рунда затворени;
              модулът е frozen с deterministic SHA-256 manifest.
            </p>
          </div>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700">
            <Lock className="w-3 h-3 mr-1" /> FROZEN
          </Badge>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="py-4 text-sm text-rose-700">
              ⚠️ {error}
            </CardContent>
          </Card>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — Module status + Limitations */}
            <div className="space-y-6">
              <ModuleStatusCard version={version} />
              <LimitationsCard limitations={limitations} />
            </div>

            {/* Right columns — Activation demo + Overlay */}
            <div className="lg:col-span-2 space-y-6">
              <ActivationBandDemo />
              <PopulationOverlayBrowser />

              {/* Documentation references */}
              <Card className="border-slate-200" data-testid="docs-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5 text-slate-600" />
                    Reference Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    {[
                      "MUSCULAR_LIMITATIONS_DECLARATION.md",
                      "MUSCULAR_FREEZE_CERTIFICATE.md",
                      "MUSCULAR_PEER_REVIEW_V1_RESPONSE.md",
                      "MUSCULAR_PEER_REVIEW_V2_RESPONSE.md",
                      "MUSCULAR_PEER_REVIEW_V3_RESPONSE.md",
                      "MUSCULAR_PEER_REVIEW_V4_RESPONSE.md",
                      "MUSCULAR_PEER_REVIEW_V5_RESPONSE.md",
                      "MUSCULAR_PEER_REVIEW_V6_RESPONSE.md",
                      "MUSCULAR_POPULATION_OVERLAY_METHODOLOGY.md",
                      "MUSCULAR_PRE_PEER_REVIEW_PACK.md",
                      "MUSCULAR_DOMINANCE_AND_SAFETY.md",
                    ].map((d) => (
                      <div
                        key={d}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 rounded border border-slate-100 font-mono text-[10px] text-slate-600"
                      >
                        <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{d}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
